import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Bell, Menu, Smartphone } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { emitEvent, EventConstants } from "@/lib/event-emitter";
import useAuth, { AuthMode } from "@/context/auth-provider";
import { queryClient } from "@/hooks/use-query-client";
import type { Demographics, Relation } from "@/model/demographics";
import { QueryKeys, STORAGE, STORAGE_KEY } from "@/constants";
import MfaContainer from "../containers/mfa-container";
import ChangePassword from "./change-password";
import EditAccountContainer from "../containers/editAccount-container";
import DataAccessManagement from "./data-access/data-access-management";
import MyPcp from "../my-pcp";
import ScreenRouteNames from "@/constants/screen-route-names";
import { usePcpInfo } from "@/hooks/use-pcpInfo";
import useUserInfo from "@/hooks/use-user-info";
import {
  PRODUCT_NAME,
  THIRD_PARTY_INTEGRATION,
  getEbixUrl,
  getGroupIdLabel,
} from "@/lib/client-utils";
import { useFeatures } from "@/hooks/use-features";
import useGuestCheckSinglePage from "@/hooks/use-guest-check-singlepage";
import type { CourtOrderDependentType } from "@/model/court-order-dependents";
import { openMobileView } from "@/signals/mobile-view-signal";
import { useGroupProvider } from "@/context/group-provider";
import { useUiLabels } from "@/hooks/use-ui-labels";
import { lowerCaseComparison } from "@/lib/string-util";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import IDENTIFIERS from "@/constants/test_id";
import type { GlobalFlag } from "@/model/global-flag";
import ManageEob from "./paperlessEob/ManageEob";
import { useSelector } from "react-redux";
import { selectNotificationUnreadCount } from "@/store/notification/notification-slice";
import PlanCoverageSelector from "./my-benefit/plan-coverage-selector";
import { isCoverageActiveOrExtended } from "@/lib/utils";
import { InviteUser } from "../invite-user/invite-user";
import { getItem } from "@/lib/storage-utils";

// @ts-expect-error -- untyped module
const OldWebsiteUrl = import.meta.env.VITE_BASE_ZCONNECT_URL;
// @ts-expect-error -- untyped module
const VITE_BASE_ZCONNECT_V4_URL = import.meta.env.VITE_BASE_ZCONNECT_V4_URL;

export type IDialogBox = {
  dialogOpen: boolean;
  setDialogOpen: (dialogOpen: boolean) => void;
  /** For logging: dialog name, e.g. "Change Password" */
  logDialogName?: string;
  /** For logging: what opened the dialog, e.g. "Header menu" */
  logTrigger?: string;
};

const Header: React.FC = () => {
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showAccountInfo, setShowAccountInfo] = useState(false);
  const [showAccessManagementDialog, setShowAccessManagementDialog] =
    useState<boolean>(false);
  const [showPcpInfo, setShowPcpInfo] = useState(false);
  const [showMfaDialog, setShowMfaDialog] = useState(false);
  const [showManageEob, setShowManageEob] = useState(false);
  const [showInviteUserDialog, setShowInviteUserDialog] = useState(false);

  const userInfo = useUserInfo();
  const { appName } = getItem(STORAGE_KEY.PRODUCT_CONFIG, STORAGE.SESSION);
  const { authData } = useAuth();
  const menuItemsQuery = useFeatures();
  useUiLabels();
  const dependents =
    (queryClient.getQueryData([QueryKeys.DEPENDENTS]) as Relation[]) ?? [];
  const hasDependent = dependents.length > 0;

  const SKINNY_WEB_URL = lowerCaseComparison(userInfo.groupId, "Z5DEMO_F")
    ? VITE_BASE_ZCONNECT_V4_URL
    : OldWebsiteUrl;

  const profileMenuItemStyle =
    "flex h-fit w-full justify-between bg-transparent p-0 font-normal text-black shadow-none hover:bg-transparent";

  const showManageEobFlag = (
    queryClient.getQueryData([QueryKeys.GLOBAL_FLAGS, userInfo.username]) as
      | { data?: GlobalFlag[] }
      | undefined
  )?.data?.[0]?.showManageEob;

  const notificationUnreadCount = useSelector(selectNotificationUnreadCount);

  const idCard =
    menuItemsQuery.data?.find(
      (item) => item.action?.web_route === ScreenRouteNames.ID_CARD.path,
    )?.name ?? "ID Card";

  const pcpQuery = usePcpInfo();
  const { toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  const { restrictPage, showSmartphone } = useGuestCheckSinglePage();

  const { groupData } = useGroupProvider();

  const isEbixClient = lowerCaseComparison(
    groupData?.login_protocol,
    THIRD_PARTY_INTEGRATION.EBIX,
  );

  // console.log("## restrictPage header", restrictPage);

  const demographicsData = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    authData?.user?.username,
  ]) as Demographics | undefined;

  const courtOrderDependents: CourtOrderDependentType[] | undefined =
    queryClient.getQueryData([QueryKeys.COURT_ORDER_DEPENDENTS]);

  return (
    <header className="header hide-from-third-app relative z-50 min-w-[320px]">
      <div className="flex flex-row items-center gap-2">
        {/* 4.1.2 Name, Role, Value — A: an icon-only button renders an <svg> with no text,
            so this was announced as just "button". */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label="Open navigation menu"
          onClick={toggleSidebar}
        >
          <Menu aria-hidden="true" className="size-5" />
        </Button>

        {/*
          2.1.1 Keyboard — A / 4.1.2 — A. The logo was a <div onClick> that navigates home:
          not in the tab order, no role, no Enter/Space activation, so keyboard users had no
          "back to home" affordance at all. The image is decorative because the button's
          accessible name now carries the meaning (1.1.1 — A: alt="Login" was also simply
          wrong for a client logo, and rendered with src={undefined} when absent).
        */}
        <button
          type="button"
          aria-label="Go to home page"
          className="flex h-10 max-w-40 cursor-pointer flex-row items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={() => {
            if (lowerCaseComparison(authData?.mode, AuthMode.MEMBER)) {
              navigate(`/${ScreenRouteNames.HOME.path}`);
            } else {
              navigate(`/${authData?.user?.route}`);
            }
          }}
        >
          {Boolean(groupData?.client_logo) && (
            <img
              src={`data:image/png;base64,${groupData?.client_logo}`}
              alt=""
              className="h-10 max-h-10 object-contain"
            />
          )}
        </button>
      </div>

      <div className="grid auto-cols-max grid-flow-col items-center">
        {userInfo.planCoverageInfo.filter(isCoverageActiveOrExtended).length >
        1 ? (
          <PlanCoverageSelector />
        ) : null}
        <div className="flex flex-row gap-4 px-2 sm:px-6">
          {/* <MessageSquare color="#737373" /> */}
          {showSmartphone && (
            // 2.1.1 Keyboard — A / 4.1.2 — A: was a bare Lucide <svg> with an onClick,
            // which is not focusable and has no role or name.
            <button
              type="button"
              aria-label="Open the mobile app view"
              className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              onClick={() => {
                const url = `${SKINNY_WEB_URL}/guest-login?redirectKey=${window.sessionStorage.getItem("redirectKey")}`;
                openMobileView(url);
              }}
            >
              <Smartphone aria-hidden="true" color="#737373" />
            </button>
          )}

          {!restrictPage && (
            /*
              2.1.1 Keyboard — A / 4.1.2 — A: notifications were a <div onClick>, so a
              keyboard user could not reach them. 1.4.1 Use of Color — A: the unread count
              was a bare number in a coloured badge with no text explaining what it counts;
              the accessible name now states it.
            */
            <button
              type="button"
              aria-label={
                notificationUnreadCount > 0
                  ? `Notifications, ${notificationUnreadCount} unread`
                  : "Notifications"
              }
              className="relative cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              onClick={() => navigate(`/${ScreenRouteNames.NOTIFICATION.path}`)}
            >
              <Bell aria-hidden="true" color="#737373" />
              {notificationUnreadCount > 0 && (
                <Badge
                  aria-hidden="true"
                  className="absolute -right-2 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-yellow-500 px-1 text-[10px] font-bold leading-none text-black shadow-sm"
                >
                  {notificationUnreadCount > 9 ? "9+" : notificationUnreadCount}
                </Badge>
              )}
            </button>
          )}
        </div>

        <DropdownMenu modal={false}>
          <DropdownMenuTrigger>
            <div className="sm:px-2">
              <Avatar>
                {/* <AvatarImage src={demographicsData?.photoUrl} /> */}
                <AvatarFallback>
                  {demographicsData?.firstName?.charAt(0)}
                  {demographicsData?.lastName?.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="max-h-[70vh] min-w-[300px] overflow-y-auto rounded-md bg-white p-[5px] shadow-md"
            sideOffset={5}
            data-hj-suppress
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-2">
                <p className="text-sm font-semibold">
                  {demographicsData?.firstName} {demographicsData?.lastName}
                </p>
                <span className="text-xs text-muted-foreground">
                  <label className="font-bold">Group Name: </label>
                  {demographicsData?.divisionName.toUpperCase()}
                </span>
                <span className="text-xs text-muted-foreground">
                  <label className="font-bold">{`${getGroupIdLabel(userInfo?.groupId)}:`}</label>{" "}
                  {demographicsData?.divisionId}
                </span>
                <span className="text-xs text-muted-foreground">
                  <label className="font-bold">Member ID:</label>{" "}
                  {demographicsData?.plainEmployeeId}
                </span>
              </div>
            </DropdownMenuLabel>

            {!restrictPage && (
              <>
                <DropdownMenuSeparator className="bg-gray-300" />
                <DropdownMenuLabel className="font-bold">
                  Tools
                </DropdownMenuLabel>
                <DropdownMenuItem
                  className="cursor-pointer focus:bg-primary-extraLight"
                  onClick={() => navigate(`/${ScreenRouteNames.ID_CARD.path}`)}
                >
                  <Button
                    type="button"
                    className={profileMenuItemStyle}
                    data-testid={IDENTIFIERS.PROFILE.ID_CARD}
                  >
                    <p>View My {idCard}</p>
                  </Button>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer focus:bg-primary-extraLight"
                  onClick={() =>
                    navigate(`/${ScreenRouteNames.MY_BENEFIT.path}`)
                  }
                >
                  <Button
                    type="button"
                    className={profileMenuItemStyle}
                    data-testid={IDENTIFIERS.PROFILE.MY_BENEFIT}
                  >
                    <p>My Benefit Document</p>
                  </Button>
                </DropdownMenuItem>

                {showManageEobFlag && (
                  <DropdownMenuItem
                    className="cursor-pointer focus:bg-primary-extraLight"
                    onClick={() => setShowManageEob(true)}
                  >
                    <Button
                      type="button"
                      className={profileMenuItemStyle}
                      data-testid={IDENTIFIERS.PROFILE.MANAGE_EOB}
                    >
                      <p>Manage EOBs</p>
                    </Button>
                  </DropdownMenuItem>
                )}

                {lowerCaseComparison(authData?.mode, AuthMode.MEMBER) && (
                  <>
                    <DropdownMenuSeparator className="bg-gray-300" />
                    <DropdownMenuLabel className="font-bold">
                      Settings
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      className="cursor-pointer focus:bg-primary-extraLight"
                      onClick={() => setShowMfaDialog(true)}
                    >
                      <Button
                        type="button"
                        className={profileMenuItemStyle}
                        data-testid={IDENTIFIERS.PROFILE.UPDATE_MFA}
                      >
                        <p>Update Multi-Factor Authentication</p>
                      </Button>
                    </DropdownMenuItem>
                  </>
                )}

                {/* <DropdownMenuSeparator className="bg-gray-300" />
              <DropdownMenuLabel className="font-bold">
                Biometrics
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => navigate("/home/biometrics")}>
                <div className="flex w-full justify-between">
                  <p>Biometrics</p>
                </div>
              </DropdownMenuItem> */}

                <DropdownMenuSeparator className="bg-gray-300" />
                <DropdownMenuLabel className="font-bold">
                  My Coverage & Data Management
                </DropdownMenuLabel>
                <DropdownMenuLabel className="font-normal">
                  {demographicsData?.planDesc}
                </DropdownMenuLabel>
                {!courtOrderDependents?.some(
                  (d) => d.memberId === userInfo.memberId,
                ) && demographicsData?.hipaaProtected ? (
                  <DropdownMenuItem
                    className="cursor-pointer focus:bg-primary-extraLight"
                    onClick={() =>
                      setShowAccessManagementDialog(!showAccessManagementDialog)
                    }
                  >
                    <Button
                      type="button"
                      className={profileMenuItemStyle}
                      data-testid={IDENTIFIERS.PROFILE.OTHER_MEMBERS}
                    >
                      <p>Other Members</p>
                    </Button>
                  </DropdownMenuItem>
                ) : null}

                <DropdownMenuSeparator className="bg-gray-300" />
                <DropdownMenuLabel className="font-bold">
                  Account
                </DropdownMenuLabel>
                <DropdownMenuItem
                  className="cursor-pointer focus:bg-primary-extraLight"
                  onClick={() => setShowAccountInfo(!showAccountInfo)}
                >
                  <Button
                    type="button"
                    className={profileMenuItemStyle}
                    data-testid={IDENTIFIERS.PROFILE.MY_ACCOUNT}
                  >
                    <p>My Account</p>
                  </Button>
                </DropdownMenuItem>

                {lowerCaseComparison(authData?.mode, AuthMode.MEMBER) && (
                  <DropdownMenuItem
                    className="cursor-pointer focus:bg-primary-extraLight"
                    onClick={() => {
                      if (isEbixClient && groupData?.client_code) {
                        window.open(
                          getEbixUrl(groupData?.client_code),
                          "_blank",
                        );
                      } else {
                        setShowPasswordDialog(!showPasswordDialog);
                      }
                    }}
                  >
                    <Button
                      type="button"
                      className={profileMenuItemStyle}
                      data-testid={IDENTIFIERS.PROFILE.CHANGE_PASSWORD}
                    >
                      <p>Change Password</p>
                    </Button>
                  </DropdownMenuItem>
                )}

                {hasDependent && (
                  <DropdownMenuItem
                    className="cursor-pointer focus:bg-primary-extraLight"
                    onClick={() => {
                      setShowInviteUserDialog(!showInviteUserDialog);
                    }}
                  >
                    <Button
                      type="button"
                      className={profileMenuItemStyle}
                      data-testid={IDENTIFIERS.PROFILE.INVITE_USER}
                    >
                      <p>Invite User to {appName || PRODUCT_NAME.ZCONNECT}</p>
                    </Button>
                  </DropdownMenuItem>
                )}

                {pcpQuery.data?.results?.length > 0 && (
                  <DropdownMenuItem
                    className="cursor-pointer focus:bg-primary-extraLight"
                    onClick={() => setShowPcpInfo(!showPcpInfo)}
                  >
                    <Button
                      type="button"
                      className={profileMenuItemStyle}
                      data-testid={IDENTIFIERS.PROFILE.MY_PCP}
                    >
                      <p>My PCP</p>
                    </Button>
                  </DropdownMenuItem>
                )}

                {lowerCaseComparison(authData?.mode, AuthMode.MEMBER) && (
                  <>
                    <DropdownMenuSeparator className="bg-gray-300" />
                    <DropdownMenuItem
                      className="cursor-pointer focus:bg-primary-extraLight"
                      onClick={() => emitEvent(EventConstants.LOGOUT)}
                    >
                      <Button
                        type="button"
                        className={profileMenuItemStyle}
                        data-testid={IDENTIFIERS.PROFILE.LOG_OUT}
                      >
                        <p>Log out</p>
                      </Button>
                    </DropdownMenuItem>
                  </>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <ChangePassword
          dialogOpen={showPasswordDialog}
          setDialogOpen={setShowPasswordDialog}
          logDialogName="Change Password"
          logTrigger="Profile menu"
        />
        <MfaContainer
          open={showMfaDialog}
          onOpenChange={setShowMfaDialog}
          logDialogName="Multi-Factor Authentication"
          logTrigger="Profile menu"
        />

        <EditAccountContainer
          open={showAccountInfo}
          onOpenChange={setShowAccountInfo}
          logTag={LogTags.ACCOUNT_DETAILS}
          logCategory={LogCategories.PROFILE_UPDATE}
          logSubCategory={LogSubCategories.NONE}
          logDialogName="My Account"
          logTrigger="Profile menu"
        />

        <DataAccessManagement
          dialogOpen={showAccessManagementDialog}
          setDialogOpen={setShowAccessManagementDialog}
          logDialogName={LogCategories.DATA_ACCESS_MANAGEMENT}
          logTrigger="Profile menu"
        />

        <MyPcp
          dialogOpen={showPcpInfo}
          setDialogOpen={setShowPcpInfo}
          logDialogName="My PCP"
          logTrigger="Profile menu"
        />

        {showManageEob && (
          <ManageEob
            dialogOpen={showManageEob}
            setDialogOpen={setShowManageEob}
            logDialogName={LogSubCategories.MANAGE_EOB} //move it to constant may use LogCategories
            logTrigger="Profile menu"
          />
        )}

        <InviteUser
          dialogOpen={showInviteUserDialog}
          setDialogOpen={setShowInviteUserDialog}
          logDialogName={LogSubCategories.INVITE_USER}
          logTrigger="Profile menu"
        />
      </div>
    </header>
  );
};

export default Header;
